/*
 * Copyright 2021 Google LLC. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as React from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import { createCustomEqual } from 'fast-equals';
import { isLatLngLiteral } from '@googlemaps/typescript-guards';
import GoogleMapsUtils from 'services/GoogleMapsUtils';
import currentLocationIcon from 'assets/img/icons/current-location.png';
import { useEffect, useMemo } from 'react';

interface GoogleMapWrapperProps {
  latLngs?: google.maps.LatLng[];
  center?: google.maps.LatLng;
  currentPosition?: google.maps.LatLng;
  zoom?: number;
  panTo?: google.maps.LatLng;
  getInfo?: (latLng: google.maps.LatLng) => string;
  onClick?: (latLng: google.maps.LatLng) => void;
}

const GoogleMapWrapper: React.FC<GoogleMapWrapperProps> = ({
  latLngs = [],
  center,
  zoom,
  panTo,
  currentPosition,
  getInfo,
  onClick,
}) => {
  const key: string = GoogleMapsUtils.getAPIKey();
  return (
    <Wrapper apiKey={key}>
      <Map center={center} zoom={zoom} disableDefaultUI={true} panTo={panTo}>
        {latLngs.map((latLng, index) => (
          <Marker
            position={latLng}
            getInfo={getInfo}
            onClick={onClick}
            key={`${latLng.lat()}-${latLng.lng()}-${index}`}
            selected={panTo?.lat() === latLng?.lat() && panTo?.lng() === latLng?.lng()}
          />
        ))}
        {currentPosition && (
          <Marker
            key="current-position"
            position={currentPosition}
            icon={{ url: currentLocationIcon }}
          />
        )}
      </Map>
    </Wrapper>
  );
};

export default GoogleMapWrapper;

interface MapProps extends google.maps.MapOptions {
  style?: { [key: string]: string };
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onIdle?: (map: google.maps.Map) => void;
  children?: React.ReactNode;
  panTo?: google.maps.LatLng;
}

const Map: React.FC<MapProps> = ({ onClick, onIdle, children, style, panTo, ...options }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<google.maps.Map>();

  React.useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {}));
    }
  }, [ref, map]);

  React.useEffect(() => {
    if (panTo) {
      map?.panTo(panTo);
    }
  }, [map, panTo]);

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      //@ts-expect-error
      map.setOptions({ ...options, myLocationEnabled: true });
      //turn on my location
    }
  }, [map, options]);

  React.useEffect(() => {
    if (map) {
      ['click', 'idle'].forEach((eventName) => google.maps.event.clearListeners(map, eventName));

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle]);

  return (
    <>
      <div ref={ref} style={style} className="google-map" />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          // @ts-ignore
          return React.cloneElement(child, { map });
        }
      })}
    </>
  );
};

interface MarkerProps extends google.maps.MarkerOptions {
  getInfo?: (latLng: google.maps.LatLng) => string;
  onClick?: (latLng: google.maps.LatLng) => void;
  selected?: boolean;
}

const Marker: React.FC<MarkerProps> = (options) => {
  const [marker, setMarker] = React.useState<google.maps.Marker>();
  const [infoWindow, setInfoWindow] = React.useState<google.maps.InfoWindow>();
  const [hover, setHover] = React.useState(false);

  const selected = options.selected;

  const info = useMemo(() => {
    if (options?.getInfo) return options?.getInfo?.(options.position as google.maps.LatLng);
  }, [options?.getInfo]);

  useEffect(() => {
    if (info && infoWindow && (hover || selected)) {
      infoWindow.setContent(info);
      infoWindow.open(options.map, marker);
    } else if (infoWindow && !hover) {
      infoWindow.setContent();
      //@ts-expect-error
      infoWindow.close(options.map, marker);
    }
  }, [infoWindow, hover, info, selected]);

  React.useEffect(() => {
    if (!marker) {
      setMarker(new google.maps.Marker());
    }

    if (!infoWindow) {
      setInfoWindow(new google.maps.InfoWindow());
    }

    // remove marker from map on unmount
    return () => {
      if (marker) {
        marker.setMap(null);
      }
      if (infoWindow) {
        infoWindow.close();
      }
    };
  }, [marker, infoWindow]);

  React.useEffect(() => {
    if (marker) {
      marker.setOptions(options);

      if (options.map) {
        marker.addListener('click', () => {
          if (options.onClick) {
            options.onClick(options.position as google.maps.LatLng);
          }
        });
        marker.addListener('mouseout', () => {
          setHover(false);
        });
        marker.addListener('mouseover', () => {
          setHover(true);
        });
      }
    }
  }, [marker, options]);

  return null;
};

//@ts-expect-error
const deepCompareEqualsForMaps = createCustomEqual((deepEqual) => (a: any, b: any) => {
  if (
    isLatLngLiteral(a) ||
    a instanceof google.maps.LatLng ||
    isLatLngLiteral(b) ||
    b instanceof google.maps.LatLng
  ) {
    return a.lat() === b.lat() && a.lng() === b.lng();
  }

  // TODO extend to other types

  // @ts-expect-error use fast-equals for other objects
  return deepEqual(a, b);
});

function useDeepCompareMemoize(value: any) {
  const ref = React.useRef();

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepCompareEffectForMaps(callback: React.EffectCallback, dependencies: any[]) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize));
}
